import { FunctionComponent } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { TextField, IconButton } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { CreateTransactionModalFormInput, CreateTransactionModalFormModel } from '../CreateTransactionModal/CreateTransactionModalFormModel';
import { GenericSelect } from '@/components/inputs/GenericSelect';
import { useTranslation } from '@/lib';
import { useTransactionContext } from '../../context/TransactionContext';

interface TransactionAssetAmountFieldProps {
  control: Control<CreateTransactionModalFormInput, unknown, CreateTransactionModalFormModel>;
  errors: FieldErrors<CreateTransactionModalFormInput>;
  index: number;
  onRemove: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

export const TransactionAssetAmountField: FunctionComponent<TransactionAssetAmountFieldProps> = ({
  control,
  errors,
  index,
  onRemove,
  canRemove,
  disabled,
}) => {
  const { t } = useTranslation();
  const { getAvailableAssetTypesForIndex } = useTransactionContext();

  // Get available asset types for this specific row
  const availableAssetTypes = getAvailableAssetTypesForIndex(index);

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto_auto]">
      <Controller
        name={`assets.${index}.assetTypeId`}
        control={control}
        render={({ field }) => (
          <GenericSelect
            label={t('asset_type')}
            value={field.value}
            options={availableAssetTypes}
            onChange={(value) => field.onChange(value)}
            error={!!errors.assets?.[index]?.assetTypeId}
            helperText={errors.assets?.[index]?.assetTypeId?.message}
            disabled={disabled}
          />
        )}
      />

      <Controller
        name={`assets.${index}.amount`}
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            onChange={(e) => field.onChange(Number(e.target.value))}
            label={t('amount')}
            type="number"
            error={errors.assets?.[index]?.amount !== undefined}
            helperText={errors.assets?.[index]?.amount?.message}
            className="md:w-32"
            disabled={disabled}
            slotProps={{
              htmlInput: { min: 1 },
            }}
          />
        )}
      />

      {canRemove && !disabled && (
        <IconButton onClick={onRemove} aria-label="remove asset" className="text-red-600 dark:text-red-400">
          <Delete />
        </IconButton>
      )}
    </div>
  );
};
